import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, FileUp, FolderOpen, Link2, RefreshCw, Trash2, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { base44 } from "@/api/base44Client";
import { statementFileUrl } from "@/lib/feeSlipStatements";
import {
  addReceipt,
  buildReceipt,
  isClaimReceipt,
  isDuplicateClaim,
  linkReceipt,
  parseClaimReceipt,
  removeReceipt,
  suggestLinks,
  unlinkReceipt,
} from "@/lib/claimReceipts";
import { folderSource, markSeen, newFolderFiles, receiptIsForFirm } from "@/lib/receiptFolder";

const money = (value) => (Number(value) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
const day = (iso) => {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};

async function readPdfText(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("The file could not be read"));
    reader.readAsDataURL(file);
  });
  const { extractPdfTextWithPdfJs } = await import("@/lib/standaloneServices");
  return extractPdfTextWithPdfJs(dataUrl);
}

// Picks what a receipt is kept against: its suggestions first, or a search of everything on
// the page. `candidates` are { kind, id, label, ... }.
function LinkPicker({ suggestions, candidates, onPick, chosenId, compact = false }) {
  const [query, setQuery] = useState("");
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return suggestions.slice(0, 5);
    const words = q.split(/\s+/);
    return candidates.filter((c) => words.every((w) => c.label.toLowerCase().includes(w))).slice(0, 8);
  }, [query, suggestions, candidates]);
  return (
    <div className="space-y-1">
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by client, invoice number…"
        className={`h-7 bg-white text-[11px] ${compact ? "" : "max-w-sm"}`}
        aria-label="Search for what this receipt was claimed for"
      />
      {shown.length === 0 ? (
        <p className="text-[10px] text-slate-500">{query.trim() ? "Nothing matches." : "No suggestion - search above, or keep it in the folder."}</p>
      ) : (
        <ul className="space-y-0.5">
          {shown.map((candidate) => (
            <li key={`${candidate.kind}:${candidate.id}`}>
              <button
                type="button"
                onClick={() => onPick(candidate)}
                className={`w-full rounded border px-2 py-1 text-left text-[11px] ${
                  chosenId === candidate.id ? "border-emerald-400 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                }`}
              >
                {candidate.label}
                {candidate.reasons?.length ? <span className="ml-1 text-emerald-700">· matches {candidate.reasons.join(", ")}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// The firm's Claim receipts folder (src/lib/claimReceipts.js), on the Fee Slips & Invoices
// page and the Disbursements page. Upload ATLAS claim receipts; each is read and the fee slip
// or disbursement it was claimed for is suggested; nothing is kept against anything until it
// is confirmed, and a receipt can wait in the folder until its fee slip arrives.
export default function ClaimReceiptsFolder({ store, candidates, itemWord = "fee slip", canEdit, userName, brand = "" }) {
  const { data, loaded, loadError, saving, change } = store;
  const [pending, setPending] = useState([]); // { key, file, fields, isReceipt, choice }
  const [reading, setReading] = useState(false);
  const [saving2, setSaving2] = useState(false);
  const [filter, setFilter] = useState("unlinked");
  const [linking, setLinking] = useState("");

  // The linked folder on Emma's computer (src/lib/receiptFolder.js): picked up from on opening
  // when Chrome allows it, or with Check folder.
  const [folder, setFolder] = useState({ state: "loading" });
  const sourceRef = useRef(null);

  const unlinked = data.receipts.filter((receipt) => !receipt.links.length);
  const shown = filter === "unlinked" ? unlinked : data.receipts;

  // files: File objects (chosen by hand) or { file, fingerprint } (from the linked folder).
  // From the folder, anything that is not this firm's ATLAS receipt, or is already in the
  // folder, is passed over quietly and remembered so it is not read again.
  const chooseFiles = async (files, { fromFolder = false } = {}) => {
    const list = [...(files || [])]
      .map((entry) => (entry instanceof Blob ? { file: entry, fingerprint: "" } : entry))
      .filter(({ file }) => /pdf$/i.test(file.type) || /\.pdf$/i.test(file.name));
    if (!list.length) {
      if (!fromFolder) toast.error("Choose ATLAS claim receipt PDFs.");
      return 0;
    }
    setReading(true);
    const read = [];
    const passedOver = [];
    for (const { file, fingerprint } of list) {
      let fields = {};
      let isReceipt = false;
      try {
        const textRead = await readPdfText(file);
        isReceipt = isClaimReceipt(textRead);
        fields = parseClaimReceipt(textRead);
      } catch (error) {
        console.warn("Claim receipt could not be read:", error);
      }
      const duplicate = isDuplicateClaim(data, fields.claimId);
      if (fromFolder && (!isReceipt || duplicate || !receiptIsForFirm(fields.serviceProvider, brand))) {
        if (fingerprint) passedOver.push(fingerprint);
        continue;
      }
      const suggestions = suggestLinks(fields, candidates);
      read.push({
        key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`,
        file,
        fingerprint,
        fields,
        isReceipt,
        suggestions,
        duplicate,
        // A clear best match is chosen for you; anything closer is left to you.
        choice: suggestions[0] && suggestions[0].score >= 6 && (!suggestions[1] || suggestions[1].score < suggestions[0].score)
          ? suggestions[0]
          : null,
      });
    }
    await markSeen(brand, passedOver);
    setPending((current) => {
      // The same file twice (a check while one is still waiting) is offered once.
      const waiting = new Set(current.map((item) => item.fingerprint).filter(Boolean));
      return [...current, ...read.filter((item) => !item.fingerprint || !waiting.has(item.fingerprint))];
    });
    setReading(false);
    return read.length;
  };

  const checkFolder = useCallback(async ({ interactive = false } = {}) => {
    const source = sourceRef.current;
    if (!source) return;
    setFolder((current) => ({ ...current, checking: true }));
    try {
      const { permission, files } = await newFolderFiles(source, brand, { interactive });
      if (permission !== "granted") {
        setFolder((current) => ({ ...current, state: "needs-permission", checking: false }));
        return;
      }
      const offered = await chooseFiles(files, { fromFolder: true });
      setFolder((current) => ({ ...current, state: "ready", checking: false, checkedAt: new Date(), found: offered }));
      if (interactive && !offered) toast.success("No new claim receipts in the folder.");
    } catch (error) {
      setFolder((current) => ({ ...current, checking: false }));
      if (interactive) toast.error(error.message || "The folder could not be read.");
    }
    // chooseFiles reads the latest folder data through its own closure each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand, data, candidates]);

  // Once the folder record has loaded: find out how the folder can be reached, and if Chrome
  // already allows it, look for new receipts straight away.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!canEdit || !loaded || loadError || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      const { source, reason } = await folderSource();
      if (!source) {
        setFolder({ state: reason === "frame" ? "frame" : "unsupported" });
        return;
      }
      sourceRef.current = source;
      const status = await source.status(brand).catch(() => ({ linked: false }));
      if (!status.linked) {
        setFolder({ state: "unlinked" });
        return;
      }
      setFolder({ state: status.permission === "granted" ? "ready" : "needs-permission", name: status.name });
      if (status.permission === "granted") checkFolder();
    })();
  }, [canEdit, loaded, loadError, brand, checkFolder]);

  const linkFolder = async () => {
    const source = sourceRef.current;
    if (!source) return;
    try {
      const status = await source.link(brand);
      setFolder({ state: "ready", name: status.name });
      checkFolder({ interactive: true });
    } catch (error) {
      if (error?.name !== "AbortError" && !/abort/i.test(error?.message || "")) toast.error(error.message || "The folder could not be linked.");
    }
  };

  const unlinkFolder = async () => {
    if (!window.confirm("Stop picking up receipts from this folder? The receipts already saved are kept.")) return;
    await sourceRef.current?.unlink(brand).catch(() => {});
    setFolder({ state: "unlinked" });
  };

  const saveAll = async () => {
    const toSave = pending.filter((item) => !item.duplicate);
    if (!toSave.length) return setPending([]);
    setSaving2(true);
    let saved = 0;
    try {
      for (const item of toSave) {
        const upload = await base44.integrations.Core.UploadFile({ file: item.file });
        const receipt = buildReceipt(item.fields, { fileName: item.file.name, fileUrl: upload?.file_url, filePath: upload?.storage_path }, { by: userName });
        await change((current) => addReceipt(current, receipt, item.choice));
        if (item.fingerprint) await markSeen(brand, [item.fingerprint]);
        saved += 1;
        setPending((current) => current.filter((other) => other.key !== item.key));
      }
      toast.success(`${saved} claim receipt${saved === 1 ? "" : "s"} saved.`);
      setPending([]);
    } catch (error) {
      toast.error(`${error.message || "A claim receipt could not be saved."}${saved ? ` (${saved} saved before it)` : ""}`);
    } finally {
      setSaving2(false);
    }
  };

  const run = async (apply, message) => {
    try {
      await change(apply);
      if (message) toast.success(message);
    } catch (error) {
      toast.error(error.message || "The claim receipts could not be saved.");
    }
  };

  return (
    <Card className="shadow-sm border-indigo-200">
      <CardHeader className="py-4 border-b border-indigo-100 bg-indigo-50/40">
        <CardTitle className="text-sm font-bold text-slate-800">Claim receipts</CardTitle>
        <CardDescription className="text-xs text-slate-600">
          ATLAS claim receipts, kept against the {itemWord} they were claimed for. Upload them here: each is read and its {itemWord} suggested
          from the claim reference, client and amount. A receipt waits in the folder until it is matched.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        {loadError && (
          <p className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">The claim receipts could not be loaded. Reload the page before uploading.</p>
        )}
        {canEdit && !loadError && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-indigo-700 px-3 py-2 text-[11px] font-semibold text-white hover:bg-indigo-800">
              <FileUp className="h-3.5 w-3.5" /> Upload claim receipts
              <input
                type="file"
                multiple
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={(event) => { chooseFiles(event.target.files); event.target.value = ""; }}
              />
            </label>
            {reading && <span className="text-[11px] text-slate-500">Reading…</span>}
            {folder.state === "unlinked" && (
              <Button type="button" variant="outline" onClick={linkFolder} className="h-8 gap-1 px-3 text-[11px]">
                <FolderOpen className="h-3.5 w-3.5" /> Link a folder (e.g. Documents\ATLAS Claim Receipts)
              </Button>
            )}
            {["ready", "needs-permission"].includes(folder.state) && (
              <span className="inline-flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
                <FolderOpen className="h-3.5 w-3.5 text-indigo-700" />
                Folder: <span className="font-semibold text-slate-800">{folder.name || "linked"}</span>
                {folder.state === "ready" && folder.checkedAt && (
                  <span>· checked {folder.checkedAt.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}{folder.found ? ` · ${folder.found} new` : ""}</span>
                )}
                <Button type="button" variant="outline" onClick={() => checkFolder({ interactive: true })} disabled={folder.checking} className="h-7 gap-1 px-2 text-[11px]">
                  <RefreshCw className={`h-3.5 w-3.5 ${folder.checking ? "animate-spin" : ""}`} />
                  {folder.checking ? "Checking…" : folder.state === "needs-permission" ? "Check folder" : "Check again"}
                </Button>
                <button type="button" onClick={unlinkFolder} className="text-[10px] text-slate-400 underline">unlink</button>
              </span>
            )}
            {folder.state === "frame" && (
              <span className="text-[11px] text-slate-500">
                To pick receipts up from a folder on this computer, open this page at{" "}
                <a href={typeof window !== "undefined" ? `https://portal.completelawsupport.com${window.location.pathname}` : "#"} target="_blank" rel="noopener noreferrer" className="underline">
                  portal.completelawsupport.com
                </a>.
              </span>
            )}
            {folder.state === "unsupported" && (
              <span className="text-[11px] text-slate-500">To pick receipts up from a folder on this computer, open the portal in Chrome.</span>
            )}
          </div>
        )}

        {pending.length > 0 && (
          <div className="space-y-3 rounded-lg border border-indigo-200 bg-white p-3">
            <p className="text-xs font-bold text-slate-800">Check before saving ({pending.length})</p>
            {pending.map((item) => (
              <div key={item.key} className="grid gap-2 border-t border-slate-100 pt-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                <div className="space-y-0.5 text-[11px] text-slate-600">
                  <p className="font-semibold text-slate-800">{item.fields.client || item.file.name}</p>
                  {!item.isReceipt && <p className="text-amber-700">This doesn&apos;t look like an ATLAS claim receipt.</p>}
                  {item.duplicate && <p className="font-semibold text-rose-700">Claim {item.fields.claimId} is already in the folder - it won&apos;t be saved twice.</p>}
                  <p>
                    {[item.fields.claimId && `Claim ${item.fields.claimId}`, item.fields.claimRef && `Ref ${item.fields.claimRef}`,
                      item.fields.amount && money(item.fields.amount), item.fields.submitted && `submitted ${day(item.fields.submitted)}`]
                      .filter(Boolean).join(" · ")}
                  </p>
                  <p>{[item.fields.work, item.fields.fileExt && `Aid ${item.fields.fileExt}`, item.fields.practitioner].filter(Boolean).join(" · ")}</p>
                  <button
                    type="button"
                    onClick={() => {
                      if (item.fingerprint) markSeen(brand, [item.fingerprint]);
                      setPending((current) => current.filter((other) => other.key !== item.key));
                    }}
                    className="text-[10px] font-semibold text-slate-500 underline"
                  >
                    Don&apos;t upload this one
                  </button>
                </div>
                {!item.duplicate && (
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                      Keep against: {item.choice ? <span className="normal-case text-emerald-800">{item.choice.label}</span> : <span className="normal-case text-slate-500">nothing yet (stays in the folder)</span>}
                      {item.choice && (
                        <button type="button" onClick={() => setPending((current) => current.map((other) => (other.key === item.key ? { ...other, choice: null } : other)))} className="ml-2 normal-case text-slate-500 underline">
                          clear
                        </button>
                      )}
                    </p>
                    <LinkPicker
                      suggestions={item.suggestions}
                      candidates={candidates}
                      chosenId={item.choice?.id}
                      onPick={(candidate) => setPending((current) => current.map((other) => (other.key === item.key ? { ...other, choice: candidate } : other)))}
                    />
                  </div>
                )}
              </div>
            ))}
            <div className="flex gap-2">
              <Button type="button" onClick={saveAll} disabled={saving2 || saving} className="h-8 bg-indigo-700 px-4 text-[11px] text-white hover:bg-indigo-800">
                {saving2 ? "Saving…" : `Save ${pending.filter((item) => !item.duplicate).length}`}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setPending([])} disabled={saving2} className="h-8 px-3 text-[11px]">Cancel</Button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 text-[11px]">
          <button type="button" onClick={() => setFilter("unlinked")} className={filter === "unlinked" ? "font-bold text-slate-900 underline" : "text-slate-500"}>
            Not yet matched ({unlinked.length})
          </button>
          <button type="button" onClick={() => setFilter("all")} className={filter === "all" ? "font-bold text-slate-900 underline" : "text-slate-500"}>
            All receipts ({data.receipts.length})
          </button>
        </div>

        {!loaded ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="rounded border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">
            {data.receipts.length === 0 ? "No claim receipts yet." : "Every receipt is matched."}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
            {shown.map((receipt) => {
              const url = statementFileUrl({ fileUrl: receipt.fileUrl });
              const suggestions = linking === receipt.id ? suggestLinks(receipt, candidates) : [];
              return (
                <li key={receipt.id} className="space-y-1.5 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 space-y-0.5 text-[11px] text-slate-600">
                      <p className="text-xs font-semibold text-slate-800">
                        {receipt.client || receipt.fileName} · {money(receipt.amount)}
                      </p>
                      <p>
                        {[receipt.claimId && `Claim ${receipt.claimId}`, receipt.claimRef && `Ref ${receipt.claimRef}`, receipt.submitted && `submitted ${day(receipt.submitted)}`, receipt.work]
                          .filter(Boolean).join(" · ")}
                      </p>
                      {receipt.links.map((link) => (
                        <p key={`${link.kind}:${link.id}`} className="flex items-center gap-1 text-emerald-800">
                          <Link2 className="h-3 w-3" /> {link.label}
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => run((current) => unlinkReceipt(current, receipt.id, link.kind, link.id), "No longer kept against it.")}
                              className="ml-1 text-slate-400 hover:text-rose-600"
                              aria-label={`Stop keeping this receipt against ${link.label}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </p>
                      ))}
                    </div>
                    <div className="flex items-center gap-1">
                      {url && (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex h-7 items-center gap-1 rounded border border-slate-200 px-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-50">
                          <FileText className="h-3.5 w-3.5" /> Receipt
                        </a>
                      )}
                      {canEdit && (
                        <>
                          <Button type="button" variant="outline" onClick={() => setLinking(linking === receipt.id ? "" : receipt.id)} className="h-7 px-2 text-[11px]">
                            {linking === receipt.id ? "Close" : `Keep against a ${itemWord}`}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => window.confirm(`Remove the claim receipt for ${receipt.client || receipt.fileName} from the folder?`)
                              && run((current) => removeReceipt(current, receipt.id), "Receipt removed.")}
                            className="h-7 px-2 text-slate-400 hover:text-rose-600"
                            aria-label="Remove this receipt"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {linking === receipt.id && (
                    <LinkPicker
                      compact
                      suggestions={suggestions}
                      candidates={candidates}
                      onPick={(candidate) => { setLinking(""); run((current) => linkReceipt(current, receipt.id, candidate), `Kept against ${candidate.label}.`); }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
